import { CreditCardOutlined } from "@ant-design/icons";
import { Divider, Flex, Form, Input, Radio, Select, Space } from "antd";
import { AktDatePicker } from "components/aktDatePicker";
import { useGetPaymentMethodsQuery } from "features/consumerPortalPaymentMethods/paymentMethodsAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import MastercardLogo from "assets/mastercard.png";
import VisaLogo from "assets/visa.png";
import AmexLogo from "assets/amex.png";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { detectCardType } from "common/utils";
import { useState } from "react";
import { useGetDebtorPortalConfigurationsQuery } from "features/auth/authAPI";
import { useWhiteLabel } from "providers/whiteLabelProvider";

const StyledInput = styled(Input)`
  width: 225px;
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const StyledFlex = styled(Flex)`
  margin-left: 20px;
  margin-top: 8px;
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const StyledSelect = styled(Select)`
  width: 250px !important;
  @media (max-width: 768px) {
    width: 100% !important;
    max-width: 100% !important;
  }
`;

const StyledVerticalFlex = styled(Flex)`
  border: ${(props) => (props.$selected ? "1px solid #7bb2ff !important" : "1px solid #f0f0f0")};
  padding: 16px;
  margin-bottom: 8px;
  &:hover {
    border-color: #d9d9d9;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const ResponsiveSpace = styled(Space)`
  @media (max-width: 768px) {
    gap: 0 !important;
    .ant-space-item {
      width: 100%;
    }
    .ant-form-item {
      margin-bottom: 12px;
    }
  }
  .ant-form-item {
    margin-bottom: 12px;
  }
`;

// Show two columns specifically on mobile while keeping desktop unchanged
const CompactMultiColumnSpace = styled(ResponsiveSpace)`
  @media (max-width: 768px) {
    align-items: flex-start !important; /* align columns to top so they stay even */
    gap: 12px !important; /* horizontal spacing between the two inputs */
    .ant-space-item {
      flex: 1;
    }
  }
`;

const FullWidthInput = styled(Input)`
  width: 100%;
`;

const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const FullWidthDatePicker = styled(AktDatePicker)`
  width: 100%;
`;

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
  @media (max-width: 768px) {
    .ant-radio-wrapper {
      width: 100%;
    }
  }
`;

const StyledDivider = styled(Divider)`
  font-size: 14px !important;
  margin: 8px 0 0 0 !important;
`;

const StyledDiv = styled.div`
  span {
    text-transform: capitalize;
  }
`;

const StyledCreditCardImage = styled.img`
  height: 20px;
  width: auto;
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  margin-left: 4px;
`;

const CardNumberInput = styled(Input)`
  width: 100%;
  max-width: 100%;
  @media (max-width: 768px) {
    .ant-input-suffix {
      display: flex;
      align-items: center;
      gap: 2px;
      flex-shrink: 0;
    }
  }
`;

function AddPaymentMethodForm({
  prefix = "",
  onStateChange = (...args) => {},
  onZipCodeChange = (...args) => {},
  onPaymentMethodChange = (...args) => {},
}) {
  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: paymentMethods, isLoading } = useGetPaymentMethodsQuery();
  const { slug } = useWhiteLabel();
  const { data: debtorPortalConfigurations } = useGetDebtorPortalConfigurationsQuery({
    agencySlug: slug,
  });
  const [cardNumber, setCardNumber] = useState("");
  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState("process_card");
  const disabledDate = (input) => {
    // Can not select days before today.
    return input && input < moment().startOf("day");
  };
  const isPaymentMethodEnabled = (method) => {
    const methods = debtorPortalConfigurations?.supportedPaymentMethods;
    // It is expected that the backend will always return a non-empty array for supportedPaymentMethods,
    // but this is a safeguard in case it returns null or an empty array.
    if (!methods || methods.length === 0) return false;
    return methods.includes(method);
  };

  const billingDetails = (
    <>
      {/* <Form.Item label="Type" name={[prefix, "type"]}>
                            <StyledSelect
                              allowClear
                              placeholder="Type"
                              options={constants?.addressTypes.map(({ display, value }) => ({
                                value,
                                label: display,
                              }))}
                            />
                          </Form.Item> */}
      <CompactMultiColumnSpace size="small" wrap>
        <Form.Item label="Address" name={[prefix, "address_1"]} rules={[{ required: true }]}>
          <StyledInput maxLength={100} placeholder="Address" />
        </Form.Item>
        <Form.Item label={" "} name={[prefix, "address_2"]}>
          <StyledInput maxLength={100} placeholder="Apt #, Suite, Floor (optional)" />
        </Form.Item>
      </CompactMultiColumnSpace>
      <CompactMultiColumnSpace size="large" wrap>
        <Form.Item label="City" name={[prefix, "city"]} rules={[{ required: true }]}>
          <FullWidthInput maxLength={100} placeholder="City" />
        </Form.Item>
        <Form.Item label="State" name={[prefix, "state"]} rules={[{ required: true }]}>
          <FullWidthSelect
            onChange={onStateChange}
            popupMatchSelectWidth={false}
            placeholder="State"
            options={constants?.states.map(({ display, value }) => ({
              value,
              label: display,
            }))}
            filterOption={filterOption}
            showSearch
          />
        </Form.Item>
        <Form.Item
          name={[prefix, "zipCode"]}
          label="Zip Code"
          rules={[
            {
              required: true,
            },
            {
              pattern: /^\d{5}(-\d{4})?$/,
              message: "Must be a valid zip code",
            },
          ]}
        >
          <FullWidthInput
            maxLength={5}
            placeholder="Zip Code"
            onChange={(e) => onZipCodeChange(e.target.value)}
          />
        </Form.Item>
      </CompactMultiColumnSpace>
    </>
  );

  return (
    <Form.Item
      name={[prefix, "paymentMethod"]}
      rules={[{ required: true, message: "A valid payment method is required" }]}
    >
      <StyledRadioGroup
        onChange={(e) => {
          onPaymentMethodChange(e.target.value);
        }}
      >
        {isPaymentMethodEnabled("process_card") && (
          <StyledVerticalFlex $selected={currentPaymentMethod === "process_card"} vertical>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prefix
                  ? prevValues[prefix].paymentMethod !== currentValues[prefix].paymentMethod
                  : prevValues.paymentMethod !== currentValues.paymentMethod
              }
            >
              {({ getFieldValue }) => {
                const paymentMethod = getFieldValue([prefix, "paymentMethod"]);
                setCurrentPaymentMethod(paymentMethod);

                return (
                  <>
                    <Radio
                      style={{
                        fontWeight: paymentMethod === "process_card" ? "bold" : "normal",
                      }}
                      value="process_card"
                    >
                      Credit/Debit Card
                    </Radio>
                    {paymentMethod === "process_card" && (
                      <StyledFlex vertical>
                        <Form.Item
                          label="Card Number"
                          name={[prefix, "cardNumber"]}
                          rules={[{ required: true }]}
                        >
                          <CardNumberInput
                            prefix={<CreditCardOutlined />}
                            value={cardNumber}
                            onChange={(e) => setCardNumber(e.target.value)}
                            placeholder="0000 0000 0000 0000"
                            suffix={
                              <>
                                <StyledCreditCardImage
                                  src={MastercardLogo}
                                  alt="mastercard"
                                  disabled={detectCardType(cardNumber).type !== "MASTERCARD"}
                                />
                                <StyledCreditCardImage
                                  src={VisaLogo}
                                  alt="visa"
                                  disabled={detectCardType(cardNumber).type !== "VISA"}
                                />
                                <StyledCreditCardImage
                                  src={AmexLogo}
                                  alt="amex"
                                  disabled={detectCardType(cardNumber).type !== "AMEX"}
                                />
                              </>
                            }
                          />
                        </Form.Item>
                        <CompactMultiColumnSpace size="large" wrap>
                          <Form.Item
                            label="Expiration Date"
                            name={[prefix, "cardExpDate"]}
                            rules={[{ required: true }]}
                          >
                            <FullWidthDatePicker
                              picker="month"
                              placeholder="MM/YY"
                              suffixIcon={null}
                              format="MM/YY"
                              disabledDate={disabledDate}
                            />
                          </Form.Item>
                          <Form.Item
                            label="Security Code"
                            name={[prefix, "cvv"]}
                            rules={[{ required: true }]}
                          >
                            <StyledInput placeholder="CVV/CVC" />
                          </Form.Item>
                        </CompactMultiColumnSpace>
                        <Form.Item
                          label="Cardholder Name"
                          name={[prefix, "name"]}
                          rules={[{ required: true }]}
                        >
                          <StyledInput placeholder="Cardholder Name" />
                        </Form.Item>
                        <Form.Item>
                          <StyledDivider orientation="left">Billing Details</StyledDivider>
                        </Form.Item>
                        {billingDetails}
                      </StyledFlex>
                    )}
                  </>
                );
              }}
            </Form.Item>
          </StyledVerticalFlex>
        )}

        {isPaymentMethodEnabled("process_ach") && (
          <StyledVerticalFlex $selected={currentPaymentMethod === "process_ach"} vertical>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prefix
                  ? prevValues[prefix].paymentMethod !== currentValues[prefix].paymentMethod
                  : prevValues.paymentMethod !== currentValues.paymentMethod
              }
            >
              {({ getFieldValue }) => {
                const paymentMethod = getFieldValue([prefix, "paymentMethod"]);
                setCurrentPaymentMethod(paymentMethod);

                return (
                  <>
                    <Radio
                      value="process_ach"
                      style={{
                        fontWeight: paymentMethod === "process_ach" ? "bold" : "normal",
                      }}
                    >
                      Bank Account ACH
                    </Radio>
                    {paymentMethod === "process_ach" && (
                      <StyledFlex vertical>
                        <Form.Item
                          label="Name on Account"
                          name={[prefix, "name"]}
                          rules={[{ required: true }]}
                        >
                          <StyledInput placeholder="Bankholder Name" />
                        </Form.Item>
                        <Form.Item
                          label="Account Type"
                          name={[prefix, "bankAccountType"]}
                          rules={[{ required: true }]}
                        >
                          <StyledSelect
                            popupMatchSelectWidth={false}
                            placeholder="Select one..."
                            options={[
                              {
                                value: "checking",
                                label: "Checking",
                              },
                              {
                                value: "savings",
                                label: "Savings",
                              },
                            ]}
                          />
                        </Form.Item>
                        <CompactMultiColumnSpace size="large" wrap>
                          <Form.Item
                            label="Bank Name"
                            name={[prefix, "bankAccountName"]}
                            rules={[{ required: true }]}
                          >
                            <FullWidthInput placeholder="Bank Name" />
                          </Form.Item>
                          <Form.Item
                            label="Routing Number"
                            name={[prefix, "bankRoutingNumber"]}
                            rules={[{ required: true }]}
                          >
                            <FullWidthInput placeholder="Routing Number" />
                          </Form.Item>
                        </CompactMultiColumnSpace>
                        <ResponsiveSpace size="large" wrap>
                          <Form.Item
                            label="Account Number"
                            name={[prefix, "bankAccountNumber"]}
                            rules={[{ required: true }]}
                          >
                            <FullWidthInput placeholder="Account Number" />
                          </Form.Item>
                          <Form.Item
                            label="Confirm Account Number"
                            name={[prefix, "confirmBankAccountNumber"]}
                            rules={[{ required: true }]}
                          >
                            <FullWidthInput placeholder="Account Number" />
                          </Form.Item>
                        </ResponsiveSpace>
                        <Form.Item>
                          <StyledDivider orientation="left">
                            Account Holder Information
                          </StyledDivider>
                        </Form.Item>
                        {billingDetails}
                      </StyledFlex>
                    )}
                  </>
                );
              }}
            </Form.Item>
          </StyledVerticalFlex>
        )}
        {prefix && (
          <StyledVerticalFlex $selected={currentPaymentMethod === "existingPaymentMethod"} vertical>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues[prefix].paymentMethod !== currentValues[prefix].paymentMethod
              }
            >
              {({ getFieldValue }) => {
                const paymentMethod = getFieldValue([prefix, "paymentMethod"]);
                setCurrentPaymentMethod(paymentMethod);
                return (
                  <>
                    <Radio
                      value="existingPaymentMethod"
                      style={{
                        fontWeight: paymentMethod === "existingPaymentMethod" ? "bold" : "normal",
                      }}
                    >
                      Existing Payment Method
                    </Radio>
                    {paymentMethod === "existingPaymentMethod" && (
                      <StyledFlex vertical>
                        {paymentMethods?.length > 0 ? (
                          <Form.Item
                            name="paymentMethodUuid"
                            label="Payment Method"
                            rules={[{ required: true, message: "Please select a payment method" }]}
                          >
                            <StyledSelect
                              allowClear
                              popupMatchSelectWidth={false}
                              placeholder="Select Payment Method"
                              loading={isLoading}
                              onChange={(value) => onPaymentMethodChange(value)}
                              options={paymentMethods?.map((method) => ({
                                value: method.uuid,
                                label:
                                  method.type === "process_ach" ? (
                                    <StyledDiv>
                                      <span>{method.bankAccountType}</span> account ••••
                                      {method.bankAccountLast4Digits}
                                    </StyledDiv>
                                  ) : (
                                    `Card ending in •••• ${method.cardLast4Digits}`
                                  ),
                              }))}
                            />
                          </Form.Item>
                        ) : (
                          <Form.Item>
                            <span>
                              No payment methods found. Please add a{" "}
                              <Link to="/payments/payment-methods">payment method</Link> first.
                            </span>
                          </Form.Item>
                        )}
                      </StyledFlex>
                    )}
                  </>
                );
              }}
            </Form.Item>
          </StyledVerticalFlex>
        )}
      </StyledRadioGroup>
    </Form.Item>
  );
}

export default AddPaymentMethodForm;
